/**
 * @fileoverview Playlist-based music player hosted in the "Background Music" main-panel tab.
 *
 * The user chooses among several playlists via a dropdown:
 *  - The special, non-deletable "User Playlist": an editable, serialized list of arbitrary track urls
 *    plus session-only local files. Individual mp3 links clicked in a PDF land here.
 *  - Any number of m3u playlists: each a url pointing at an .m3u file of track urls. These are read-only
 *    (the user can add/remove the *playlist*, but not edit its tracks). The user's known m3u playlists
 *    persist globally; worlds contribute m3u playlists that merge into the list on load.
 *
 * Looping plays the active playlist top-to-bottom and repeats the whole list. A separate "stay on and
 * loop current song" toggle instead repeats just the current track.
 *
 * Persistence (app-scoped, via StateManager): the User Playlist's remote tracks (bgMusicUserTracks),
 * the known m3u playlists (bgMusicPlaylists), the last-selected playlist (bgMusicActivePlaylist), and
 * the stay-and-loop flag (bgMusicStayAndLoop). Local-file object urls cannot be replayed across
 * sessions, so they are kept in memory only (isLocal=true) and dropped on reload.
 * @module ui/BackgroundMusicPlayer
 */

import { PdfViewer } from './PdfViewer.js';
import { loadTextDataFromPath, fixRelativeChildPathWithParentFilePath, urlExtension, urlFileName, fileNameExtension } from '../utils/jrfuncs.js';


const USER_PLAYLIST_ID = 'user';
const USER_PLAYLIST_LABEL = 'User Playlist';
// separator shown between a playlist's label and its url in the dropdown. Non-breaking spaces keep the
// padding from being collapsed by the native <select> rendering, so the two parts stay clearly apart.
const PLAYLIST_LABEL_URL_SEP = '   -   ';


/**
 * A case viewer link (/cases/mviewer?pg=... or ?path=...) names the audio file it shows rather than being it: hand
 * back the file's own url, as the PDF audio-link path does (PdfViewer.resolveMviewerFileUrl); any other url unchanged.
 * Pasted, dropped and m3u-listed tracks and the titles derived from them all go through this, so such a link plays
 * instead of handing the player the viewer page (review item 139).
 * @param {string} url
 * @returns {string}
 */
function unwrapViewerLink(url) {
    return (url && PdfViewer.resolveMviewerFileUrl(url)) || url;
}


export class BackgroundMusicPlayer {
    /**
     * @param {HTMLElement} paneEl - The tab pane element to render the manager into
     * @param {Object} options
     * @param {Object} options.stateManager - StateManager for persistence
     */
    constructor(paneEl, options = {}) {
        this.paneEl = paneEl;
        this.stateManager = options.stateManager || null;

        // User Playlist tracks, newest first: {title, url, isLocal}. Remote tracks are persisted; local
        // (isLocal=true) tracks are session-only and live only here.
        this.userTracks = this._loadUserTracks();

        // Playlist descriptors built from the User Playlist + the known m3u playlists.
        this.playlists = [];
        // id of the active (selected) playlist: USER_PLAYLIST_ID or an m3u url.
        this.activePlaylistId = this.stateManager ? this.stateManager.getMusicActivePlaylistId() : USER_PLAYLIST_ID;

        // Per-session cache of fetched/parsed m3u playlists: url -> {status, tracks, error, promise}.
        this.m3uCache = new Map();

        // url of the track currently loaded into the media element (null when stopped).
        this.currentUrl = null;
        // Snapshot of the track list the current song belongs to, captured at play time so advancing
        // follows that playlist even if the user changes the dropdown selection afterwards.
        this.currentPlaylistTracks = [];
        // url of the User Playlist row currently being drag-reordered (null when not dragging).
        this._dragSourceUrl = null;

        // DOM refs (created lazily in _build()).
        this.rootEl = null;
        this.selectEl = null;
        this.removePlaylistBtnEl = null;
        this.addPlaylistInputEl = null;
        this.trackTbodyEl = null;
        this.tableWrapEl = null;
        this.userAddRowEl = null;
        this.urlInputEl = null;
        this.fileInputEl = null;
        this.stayLoopCheckboxEl = null;
        this.stayLoopLabelEl = null;
        this.mediaContainerEl = null;
        this.mediaEl = null;

        this._built = false;
        this._onEnded = () => this._handleTrackEnded();
    }


    // ==========================================
    // Public API
    // ==========================================


    /**
     * Builds (if needed) and renders the player. Does not start playback.
     */
    render() {
        this._build();
        this.playlists = this._buildPlaylistsModel();
        this._renderSelector();
        this._renderActivePlaylist();
    }


    /**
     * Rebuilds the playlist selector from the persisted known list (e.g. after a world switch merged
     * new playlists). Safe to call before the UI has been built.
     */
    refreshPlaylists() {
        if (!this._built) { return; }
        this.playlists = this._buildPlaylistsModel();
        this._renderSelector();
        this._renderActivePlaylist();
    }


    /**
     * Adds an m3u playlist to the known list (if new), selects it, and starts playing its first track.
     * Used by the PDF .m3u-link path.
     * @param {string} m3uUrl
     * @param {string} [label]
     */
    async playPlaylistUrl(m3uUrl, label) {
        if (!m3uUrl) { return; }
        this._build();
        if (this.stateManager) {
            this.stateManager.addMusicPlaylist(label || this._deriveTitle(m3uUrl), m3uUrl);
            this.stateManager.setMusicActivePlaylistId(m3uUrl);
        }
        this.activePlaylistId = m3uUrl;
        this.playlists = this._buildPlaylistsModel();
        this._renderSelector();
        this._renderActivePlaylist();

        const entry = await this._ensureM3uLoaded(m3uUrl);
        if (entry && entry.status === 'ready' && entry.tracks.length) {
            this.currentPlaylistTracks = entry.tracks.slice();
            this._playTrack(entry.tracks[0]);
        }
    }


    /**
     * Adds a single track to the User Playlist (if new), selects the User Playlist, and plays it.
     * Used by the PDF audio-link path. With {loop:true} (legacy ?backgroundLoop links) it also turns on
     * "stay on and loop current song".
     * @param {string} url
     * @param {Object} [opts]
     * @param {boolean} [opts.loop=false]
     */
    playTrackUrl(url, opts = {}) {
        if (!url) { return; }
        this._build();
        this._addUserTrack(this._deriveTitle(url), url, false);
        this.activePlaylistId = USER_PLAYLIST_ID;
        if (this.stateManager) { this.stateManager.setMusicActivePlaylistId(USER_PLAYLIST_ID); }
        if (opts.loop) { this._setStayAndLoop(true); }
        this.playlists = this._buildPlaylistsModel();
        this._renderSelector();
        this._renderActivePlaylist();
        this._playFromActive(url);
    }


    /**
     * Stops playback and tears down the media element. Leaves the playlists/state intact.
     */
    stop() {
        if (this.mediaEl) {
            try {
                this.mediaEl.pause();
                this.mediaEl.removeEventListener('ended', this._onEnded);
                this.mediaEl.removeAttribute('src');
                this.mediaEl.load();
            } catch (e) { /* ignore */ }
        }
        if (this.mediaContainerEl) { this.mediaContainerEl.innerHTML = ''; }
        this.mediaEl = null;
        this.currentUrl = null;
    }


    // ==========================================
    // Playlist model
    // ==========================================


    /**
     * Builds the playlist descriptors: the User Playlist first, then the known m3u playlists.
     * @returns {Array<{id, type, label, url}>}
     * @private
     */
    _buildPlaylistsModel() {
        const descriptors = [{ id: USER_PLAYLIST_ID, type: 'user', label: USER_PLAYLIST_LABEL, url: null }];
        const known = this.stateManager ? (this.stateManager.getMusicPlaylists() || []) : [];
        for (const p of known) {
            if (!p || !p.url) { continue; }
            descriptors.push({ id: p.url, type: 'm3u', label: p.label || this._deriveTitle(p.url), url: p.url });
        }
        return descriptors;
    }


    /** @private */
    _getActivePlaylist() {
        return this.playlists.find(p => p.id === this.activePlaylistId) || this.playlists[0];
    }


    /**
     * Returns the resolved track list for the active playlist (User Playlist tracks, or the cached m3u
     * tracks; empty while an m3u is loading or errored).
     * @returns {Array<{title, url}>}
     * @private
     */
    _getActivePlaylistTracks() {
        const pl = this._getActivePlaylist();
        if (!pl || pl.type === 'user') { return this.userTracks; }
        const entry = this.m3uCache.get(pl.url);
        return (entry && entry.status === 'ready') ? entry.tracks : [];
    }


    /** @private */
    _loadUserTracks() {
        if (!this.stateManager) { return []; }
        const stored = this.stateManager.getBackgroundMusicUserTracks() || [];
        return stored.map(t => ({ title: t.title, url: t.url, isLocal: false }));
    }


    /** Persists the remote subset of userTracks (local object urls are never stored). @private */
    _persistUserTracks() {
        if (!this.stateManager) { return; }
        const remote = this.userTracks
            .filter(t => !t.isLocal)
            .map(t => ({ title: t.title, url: t.url }));
        this.stateManager.setBackgroundMusicUserTracks(remote);
    }


    /**
     * Adds (or moves to the front, deduped by url) a track in the User Playlist; persists if remote.
     * @private
     */
    _addUserTrack(title, url, isLocal) {
        const trimmed = unwrapViewerLink((url || '').trim());
        if (!trimmed) { return; }
        this.userTracks = this.userTracks.filter(t => t.url !== trimmed);
        this.userTracks.unshift({ title: title || this._deriveTitle(trimmed), url: trimmed, isLocal: !!isLocal });
        if (!isLocal) { this._persistUserTracks(); }
    }


    /**
     * Removes a User Playlist track by url. If it was playing, plays the track after it in the User
     * Playlist (wrapping to the first), or stops when the playlist is now empty.
     * @private
     */
    _removeUserTrack(url) {
        const wasPlaying = (this.currentUrl === url);
        // (its position is noted BEFORE the removal: the track that then sits there is the next one)
        const removedIndex = this.userTracks.findIndex(t => t.url === url);
        const existing = this.userTracks[removedIndex];
        if (!existing) { return; }
        this.userTracks = this.userTracks.filter(t => t.url !== url);
        if (!existing.isLocal) {
            this._persistUserTracks();
        } else if (url.startsWith('blob:')) {
            try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
        }

        if (wasPlaying) {
            // advance within the now-shortened User Playlist to the track that took the removed one's
            // place (wrapping to the top after the last), or stop if nothing is left. (Asking
            // _findNextUrl for the url just removed found nothing and always restarted at the top.)
            this.currentPlaylistTracks = this.userTracks.slice();
            const list = this.currentPlaylistTracks;
            const next = (list.length > 0) ? list[removedIndex % list.length].url : null;
            if (next) { this._playByUrlInCurrentPlaylist(next); } else { this.stop(); }
        }
        this._renderTracks();
    }


    // ==========================================
    // m3u loading / parsing
    // ==========================================


    /**
     * Ensures the given m3u url is fetched and parsed, caching the result. Re-renders the track list
     * when the active playlist matches. Awaiting resolves once loading completes (success or error).
     * @param {string} url
     * @param {Object} [opts]
     * @param {boolean} [opts.force=false] - Re-fetch even if already cached
     * @returns {Promise<{status, tracks, error}>}
     * @private
     */
    async _ensureM3uLoaded(url, opts = {}) {
        let entry = this.m3uCache.get(url);
        if (entry && entry.status === 'ready' && !opts.force) { return entry; }
        if (entry && entry.status === 'loading' && entry.promise) { await entry.promise; return this.m3uCache.get(url); }

        entry = { status: 'loading', tracks: [], error: null, promise: null };
        this.m3uCache.set(url, entry);
        if (this._activeUrlIs(url)) { this._renderTracks(); }

        entry.promise = (async () => {
            try {
                const text = await loadTextDataFromPath(url, 'load m3u playlist');
                entry.tracks = this._parseM3u(text, url);
                entry.status = 'ready';
            } catch (e) {
                entry.error = e;
                entry.status = 'error';
            }
            if (this._activeUrlIs(url)) { this._renderTracks(); }
        })();
        await entry.promise;
        return entry;
    }


    /** @private */
    _activeUrlIs(url) {
        const pl = this._getActivePlaylist();
        return !!pl && pl.url === url;
    }


    /**
     * Parses an m3u/m3u8 playlist body into tracks. Blank lines and comments are skipped, except
     * #EXTINF:duration,Title lines, whose title applies to the following url. Relative urls resolve
     * against the playlist url.
     * @param {string} text
     * @param {string} baseUrl
     * @returns {Array<{title, url}>}
     * @private
     */
    _parseM3u(text, baseUrl) {
        const tracks = [];
        let pendingTitle = null;
        for (const raw of (text || '').split(/\r?\n/)) {
            const line = raw.trim();
            if (!line) { continue; }
            if (line.startsWith('#')) {
                const m = /^#EXTINF:[^,]*,(.*)$/i.exec(line);
                if (m) { pendingTitle = (m[1] || '').trim() || null; }
                continue;
            }
            const url = this._resolveTrackUrl(line, baseUrl);
            tracks.push({ title: pendingTitle || this._deriveTitle(url), url });
            pendingTitle = null;
        }
        return tracks;
    }


    /** Resolves a (possibly relative) m3u entry against the playlist url. @private */
    _resolveTrackUrl(line, baseUrl) {
        let resolved;
        try {
            // make the base absolute first so site-relative playlist urls (e.g. /media/...) work too
            const absBase = new URL(baseUrl, window.location.origin).href;
            resolved = new URL(line, absBase).href;
        } catch (e) {
            try { resolved = fixRelativeChildPathWithParentFilePath(baseUrl, line); } catch (e2) { resolved = line; }
        }
        return unwrapViewerLink(resolved);
    }


    // ==========================================
    // Playback engine
    // ==========================================


    /** Plays a url from the active playlist, snapshotting that playlist for advancing. @private */
    _playFromActive(url) {
        const tracks = this._getActivePlaylistTracks();
        const track = tracks.find(t => t.url === url);
        if (!track) { return; }
        this.currentPlaylistTracks = tracks.slice();
        this._playTrack(track);
    }


    /** Plays a url already within currentPlaylistTracks. @private */
    _playByUrlInCurrentPlaylist(url) {
        const track = (this.currentPlaylistTracks || []).find(t => t.url === url);
        if (!track) { return; }
        this._playTrack(track);
    }


    /** @private */
    _playTrack(track) {
        this.currentUrl = track.url;

        const isVideo = PdfViewer.MEDIA_VIDEO_EXTS.has(urlExtension(track.url));
        const stayAndLoop = this._getStayAndLoop();

        // (re)create the media element so audio/video tracks can be mixed in one playlist
        if (this.mediaEl) {
            try { this.mediaEl.pause(); this.mediaEl.removeEventListener('ended', this._onEnded); } catch (e) { /* ignore */ }
        }
        this.mediaContainerEl.innerHTML = '';
        const mediaEl = document.createElement(isVideo ? 'video' : 'audio');
        mediaEl.src = track.url;
        mediaEl.controls = true;
        mediaEl.autoplay = true;
        mediaEl.loop = stayAndLoop;
        mediaEl.className = isVideo ? 'bgmusic-video' : 'bgmusic-audio';
        mediaEl.addEventListener('ended', this._onEnded);
        this.mediaContainerEl.appendChild(mediaEl);
        this.mediaEl = mediaEl;

        // The initial play() is reached from a user gesture (PDF link click or a row click), so audio is
        // allowed; subsequent advances continue already-started playback, which browsers permit.
        const playPromise = mediaEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => { /* autoplay may be blocked/deferred by the browser */ });
        }

        this._highlightPlayingRow();
    }


    /** Fired when a track ends with loop off: advance to the next track, wrapping around the playlist. @private */
    _handleTrackEnded() {
        if (this._getStayAndLoop()) { return; }
        const next = this._findNextUrl(this.currentUrl);
        if (next) {
            this._playByUrlInCurrentPlaylist(next);
        } else {
            this.stop();
            this._renderTracks();
        }
    }


    /**
     * Returns the url of the next track after the given url within currentPlaylistTracks, wrapping to the
     * start (so the whole playlist repeats). Returns null only when the playlist is empty.
     * @param {string} fromUrl
     * @returns {string|null}
     * @private
     */
    _findNextUrl(fromUrl) {
        const list = this.currentPlaylistTracks || [];
        if (list.length === 0) { return null; }
        const startIndex = list.findIndex(t => t.url === fromUrl);
        const idx = ((startIndex < 0 ? -1 : startIndex) + 1) % list.length;
        return list[idx].url;
    }


    // ==========================================
    // Stay-and-loop helpers
    // ==========================================


    /** @private */
    _getStayAndLoop() {
        return this.stateManager ? this.stateManager.getBackgroundMusicStayAndLoop() : false;
    }


    /** @private */
    _setStayAndLoop(value) {
        if (this.stateManager) { this.stateManager.setBackgroundMusicStayAndLoop(value); }
        if (this.stayLoopCheckboxEl) { this.stayLoopCheckboxEl.checked = !!value; }
        this._updateStayLoopLabel(!!value);
        if (this.mediaEl) { this.mediaEl.loop = !!value; }
    }


    /** Highlights the "stay and loop" label (bold + red) while looping is on. @private */
    _updateStayLoopLabel(value) {
        if (this.stayLoopLabelEl) { this.stayLoopLabelEl.classList.toggle('active', !!value); }
    }


    // ==========================================
    // DOM construction & rendering
    // ==========================================


    /** Builds the static scaffold once. @private */
    _build() {
        if (this._built) { return; }

        this.paneEl.innerHTML = '';
        const root = document.createElement('div');
        root.className = 'bgmusic-manager';

        // --- playlist selector row (dropdown + remove-playlist button) ---
        const selectRow = document.createElement('div');
        selectRow.className = 'bgmusic-playlist-select-row';
        const select = document.createElement('select');
        select.className = 'form-select form-select-sm bgmusic-playlist-select';
        select.title = 'Choose a playlist';
        select.addEventListener('change', () => this._onPlaylistSelected(select.value));
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'btn btn-sm btn-outline-danger bgmusic-remove-playlist';
        removeBtn.title = 'Remove this playlist';
        removeBtn.innerHTML = '<i class="bi bi-trash"></i>';
        removeBtn.addEventListener('click', () => this._onRemovePlaylistClicked());
        selectRow.appendChild(select);
        selectRow.appendChild(removeBtn);
        this.selectEl = select;
        this.removePlaylistBtnEl = removeBtn;

        // --- add-playlist row (m3u url) ---
        const addPlaylistRow = document.createElement('div');
        addPlaylistRow.className = 'bgmusic-add-playlist-row';
        const addPlaylistInput = document.createElement('input');
        addPlaylistInput.type = 'text';
        addPlaylistInput.className = 'form-control form-control-sm bgmusic-add-playlist-input';
        addPlaylistInput.placeholder = 'Playlist .m3u URL…';
        const addPlaylistBtn = document.createElement('button');
        addPlaylistBtn.type = 'button';
        addPlaylistBtn.className = 'btn btn-sm btn-outline-secondary bgmusic-add-playlist';
        addPlaylistBtn.textContent = 'Add playlist';
        addPlaylistInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { this._onAddPlaylistClicked(); } });
        addPlaylistBtn.addEventListener('click', () => this._onAddPlaylistClicked());
        addPlaylistRow.appendChild(addPlaylistInput);
        addPlaylistRow.appendChild(addPlaylistBtn);
        this.addPlaylistInputEl = addPlaylistInput;

        // --- track table ---
        const tableWrap = document.createElement('div');
        tableWrap.className = 'bgmusic-table-wrap';
        const table = document.createElement('table');
        table.className = 'bgmusic-playlist';
        const tbody = document.createElement('tbody');
        table.appendChild(tbody);
        tableWrap.appendChild(table);
        this.trackTbodyEl = tbody;
        this.tableWrapEl = tableWrap;
        // accept files/links dragged in from outside the app (added to the User Playlist)
        tableWrap.addEventListener('dragover', (e) => this._onExternalDragOver(e));
        tableWrap.addEventListener('dragleave', (e) => this._onExternalDragLeave(e));
        tableWrap.addEventListener('drop', (e) => this._onExternalDrop(e));

        // --- User-Playlist add-track controls (shown only when the User Playlist is active) ---
        const userAddRow = document.createElement('div');
        userAddRow.className = 'bgmusic-add-row';
        const urlInput = document.createElement('input');
        urlInput.type = 'text';
        urlInput.className = 'form-control form-control-sm bgmusic-url-input';
        urlInput.placeholder = 'Audio/video URL…';
        const addUrlBtn = document.createElement('button');
        addUrlBtn.type = 'button';
        addUrlBtn.className = 'btn btn-sm btn-outline-secondary bgmusic-add-url';
        addUrlBtn.textContent = 'Add URL';
        const addFileBtn = document.createElement('button');
        addFileBtn.type = 'button';
        addFileBtn.className = 'btn btn-sm btn-outline-secondary bgmusic-add-file';
        addFileBtn.innerHTML = '<i class="bi bi-folder2-open"></i> Add local file…';
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.className = 'd-none bgmusic-file-input';
        fileInput.accept = 'audio/*,video/*';
        userAddRow.appendChild(urlInput);
        userAddRow.appendChild(addUrlBtn);
        userAddRow.appendChild(addFileBtn);
        userAddRow.appendChild(fileInput);
        this.userAddRowEl = userAddRow;
        this.urlInputEl = urlInput;
        this.fileInputEl = fileInput;
        addUrlBtn.addEventListener('click', () => this._onAddUrlClicked());
        urlInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { this._onAddUrlClicked(); } });
        addFileBtn.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', () => this._onFileChosen());

        // --- "stay and loop" toggle ---
        const topRow = document.createElement('div');
        topRow.className = 'bgmusic-controls-top form-check';
        const stayLoop = document.createElement('input');
        stayLoop.type = 'checkbox';
        stayLoop.className = 'form-check-input bgmusic-stayloop';
        stayLoop.id = 'bgmusic-stayloop';
        stayLoop.checked = this._getStayAndLoop();
        const stayLoopLabel = document.createElement('label');
        stayLoopLabel.className = 'form-check-label bgmusic-stayloop-label';
        stayLoopLabel.setAttribute('for', 'bgmusic-stayloop');
        stayLoopLabel.textContent = 'Stay on and loop current song';
        stayLoop.addEventListener('change', () => this._setStayAndLoop(stayLoop.checked));
        topRow.appendChild(stayLoop);
        topRow.appendChild(stayLoopLabel);
        this.stayLoopCheckboxEl = stayLoop;
        this.stayLoopLabelEl = stayLoopLabel;
        // bold/red the label when looping is on, to draw attention to it
        this._updateStayLoopLabel(stayLoop.checked);

        // --- media element host ---
        const mediaContainer = document.createElement('div');
        mediaContainer.className = 'bgmusic-media';
        this.mediaContainerEl = mediaContainer;

        root.appendChild(selectRow);
        root.appendChild(addPlaylistRow);
        root.appendChild(tableWrap);
        root.appendChild(userAddRow);
        root.appendChild(topRow);
        root.appendChild(mediaContainer);

        this.paneEl.appendChild(root);
        this.rootEl = root;
        this._built = true;
    }


    /** Rebuilds the playlist dropdown options and selection. @private */
    _renderSelector() {
        if (!this.selectEl) { return; }
        this.selectEl.innerHTML = '';
        for (const pl of this.playlists) {
            const opt = document.createElement('option');
            opt.value = pl.id;
            // show "label   -   full url" for m3u playlists; the User Playlist has no url, so just its label
            opt.textContent = pl.url ? `${pl.label}${PLAYLIST_LABEL_URL_SEP}${pl.url}` : pl.label;
            opt.title = pl.url ? `${pl.label} - ${pl.url}` : pl.label;
            this.selectEl.appendChild(opt);
        }
        if (!this.playlists.some(p => p.id === this.activePlaylistId)) {
            this.activePlaylistId = USER_PLAYLIST_ID;
        }
        this.selectEl.value = this.activePlaylistId;
    }


    /** Updates per-playlist affordances (remove button, user add controls) and renders its tracks. @private */
    _renderActivePlaylist() {
        const pl = this._getActivePlaylist();
        const isUser = !pl || pl.type === 'user';
        if (this.removePlaylistBtnEl) { this.removePlaylistBtnEl.style.display = isUser ? 'none' : ''; }
        if (this.userAddRowEl) { this.userAddRowEl.style.display = isUser ? '' : 'none'; }
        if (pl && pl.type === 'm3u') {
            // fire-and-forget; _ensureM3uLoaded re-renders the track list when it finishes
            this._ensureM3uLoaded(pl.url);
        }
        this._renderTracks();
    }


    /** Rebuilds the track table body for the active playlist. @private */
    _renderTracks() {
        if (!this.trackTbodyEl) { return; }
        this.trackTbodyEl.innerHTML = '';
        const pl = this._getActivePlaylist();

        if (pl && pl.type === 'm3u') {
            const entry = this.m3uCache.get(pl.url);
            if (entry && entry.status === 'loading') {
                this.trackTbodyEl.appendChild(this._buildMessageRow('Loading playlist…'));
                return;
            }
            if (entry && entry.status === 'error') {
                this.trackTbodyEl.appendChild(this._buildErrorRow(pl.url));
                return;
            }
        }

        const tracks = this._getActivePlaylistTracks();
        if (!tracks || tracks.length === 0) {
            const msg = (pl && pl.type === 'user')
                ? 'No tracks. Add a URL or local file below, or drag files/links here.'
                : 'This playlist has no tracks.';
            this.trackTbodyEl.appendChild(this._buildMessageRow(msg));
            return;
        }

        const isUser = !pl || pl.type === 'user';
        for (const track of tracks) {
            this.trackTbodyEl.appendChild(this._buildRow(track, isUser));
        }
    }


    /** @private */
    _buildMessageRow(text) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.className = 'bgmusic-empty';
        td.colSpan = 3;
        td.textContent = text;
        tr.appendChild(td);
        return tr;
    }


    /** @private */
    _buildErrorRow(url) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.className = 'bgmusic-empty bgmusic-error';
        td.colSpan = 3;
        td.textContent = 'Failed to load playlist. ';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'btn btn-sm btn-outline-secondary bgmusic-retry';
        retry.textContent = 'Retry';
        retry.addEventListener('click', () => this._ensureM3uLoaded(url, { force: true }));
        td.appendChild(retry);
        tr.appendChild(td);
        return tr;
    }


    /**
     * Builds one track row. A delete button is shown only for User Playlist tracks (isUser=true).
     * @private
     */
    _buildRow(track, isUser) {
        const tr = document.createElement('tr');
        tr.className = 'bgmusic-row' + (track.url === this.currentUrl ? ' playing' : '');
        tr.dataset.url = track.url;

        // playing indicator + title (click to play); autosizes to fit its content
        const titleTd = document.createElement('td');
        titleTd.className = 'bgmusic-cell-title';
        titleTd.title = track.title || this._deriveTitle(track.url);
        const icon = document.createElement('i');
        icon.className = (track.url === this.currentUrl) ? 'bi bi-volume-up bgmusic-row-icon' : 'bi bi-music-note bgmusic-row-icon';
        const titleSpan = document.createElement('span');
        titleSpan.className = 'bgmusic-row-title';
        titleSpan.textContent = track.title || this._deriveTitle(track.url);
        if (track.isLocal) {
            const localBadge = document.createElement('span');
            localBadge.className = 'badge bg-secondary bgmusic-local-badge';
            localBadge.textContent = 'local';
            titleSpan.appendChild(document.createTextNode(' '));
            titleSpan.appendChild(localBadge);
        }
        if (isUser) {
            // drag handle: a visual cue that User Playlist rows can be reordered by dragging
            const grip = document.createElement('i');
            grip.className = 'bi bi-grip-vertical bgmusic-drag-handle';
            grip.title = 'Drag to reorder';
            titleTd.appendChild(grip);
        }
        titleTd.appendChild(icon);
        titleTd.appendChild(titleSpan);
        titleTd.addEventListener('click', () => this._playFromActive(track.url));

        // url (click to play); takes the remaining width and truncates with an ellipsis
        const urlTd = document.createElement('td');
        urlTd.className = 'bgmusic-cell-url';
        urlTd.title = track.url;
        urlTd.textContent = track.url;
        urlTd.addEventListener('click', () => this._playFromActive(track.url));

        // delete (User Playlist only)
        const actionTd = document.createElement('td');
        actionTd.className = 'bgmusic-cell-action';
        if (isUser) {
            const delBtn = document.createElement('button');
            delBtn.type = 'button';
            delBtn.className = 'btn btn-sm btn-outline-danger bgmusic-row-delete';
            delBtn.title = 'Remove from User Playlist';
            delBtn.innerHTML = '<i class="bi bi-x"></i>';
            delBtn.addEventListener('click', (e) => { e.stopPropagation(); this._removeUserTrack(track.url); });
            actionTd.appendChild(delBtn);
        }

        tr.appendChild(titleTd);
        tr.appendChild(urlTd);
        tr.appendChild(actionTd);

        if (isUser) { this._wireRowDragAndDrop(tr, track.url); }
        return tr;
    }


    /** Wires HTML5 drag-and-drop reordering on a User Playlist row. @private */
    _wireRowDragAndDrop(tr, url) {
        tr.draggable = true;
        tr.addEventListener('dragstart', (e) => {
            this._dragSourceUrl = url;
            if (e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', url); } catch (err) { /* ignore */ }
            }
            tr.classList.add('bgmusic-dragging');
        });
        tr.addEventListener('dragend', () => {
            tr.classList.remove('bgmusic-dragging');
            this._dragSourceUrl = null;
            this._clearDropIndicators();
        });
        tr.addEventListener('dragover', (e) => {
            if (!this._dragSourceUrl || this._dragSourceUrl === url) { return; }
            e.preventDefault();
            if (e.dataTransfer) { e.dataTransfer.dropEffect = 'move'; }
            const rect = tr.getBoundingClientRect();
            const after = (e.clientY - rect.top) > rect.height / 2;
            this._clearDropIndicators();
            tr.classList.add(after ? 'bgmusic-drop-after' : 'bgmusic-drop-before');
        });
        tr.addEventListener('dragleave', () => {
            tr.classList.remove('bgmusic-drop-before', 'bgmusic-drop-after');
        });
        tr.addEventListener('drop', (e) => {
            if (!this._dragSourceUrl) { return; }
            e.preventDefault();
            const rect = tr.getBoundingClientRect();
            const after = (e.clientY - rect.top) > rect.height / 2;
            const sourceUrl = this._dragSourceUrl;
            this._clearDropIndicators();
            this._reorderUserTrack(sourceUrl, url, after);
        });
    }


    /** Clears any drop-position indicator classes from the track rows. @private */
    _clearDropIndicators() {
        if (!this.trackTbodyEl) { return; }
        this.trackTbodyEl.querySelectorAll('.bgmusic-drop-before, .bgmusic-drop-after').forEach((el) => {
            el.classList.remove('bgmusic-drop-before', 'bgmusic-drop-after');
        });
    }


    /**
     * Moves a User Playlist track to before/after a target track, persists the new order, and re-renders.
     * @param {string} sourceUrl - The dragged track's url
     * @param {string} targetUrl - The track it was dropped onto
     * @param {boolean} after - Drop below (true) or above (false) the target
     * @private
     */
    _reorderUserTrack(sourceUrl, targetUrl, after) {
        if (!sourceUrl || sourceUrl === targetUrl) { return; }
        const from = this.userTracks.findIndex(t => t.url === sourceUrl);
        if (from < 0) { return; }
        const [moved] = this.userTracks.splice(from, 1);
        let to = this.userTracks.findIndex(t => t.url === targetUrl);
        if (to < 0) { to = this.userTracks.length; } else if (after) { to += 1; }
        this.userTracks.splice(to, 0, moved);
        this._persistUserTracks();
        // keep the advancing order in sync if a User Playlist track is currently playing
        const active = this._getActivePlaylist();
        if (active && active.type === 'user' && this.currentUrl && this.userTracks.some(t => t.url === this.currentUrl)) {
            this.currentPlaylistTracks = this.userTracks.slice();
        }
        this._renderTracks();
    }


    /** Updates the playing-row highlight + icons without a full rebuild. @private */
    _highlightPlayingRow() {
        if (!this.trackTbodyEl) { return; }
        this.trackTbodyEl.querySelectorAll('.bgmusic-row').forEach((tr) => {
            const isPlaying = (tr.dataset.url === this.currentUrl);
            tr.classList.toggle('playing', isPlaying);
            const icon = tr.querySelector('.bgmusic-row-icon');
            if (icon) {
                icon.className = isPlaying ? 'bi bi-volume-up bgmusic-row-icon' : 'bi bi-music-note bgmusic-row-icon';
            }
        });
    }


    // ==========================================
    // Control handlers
    // ==========================================


    /** @private */
    _onPlaylistSelected(id) {
        this.activePlaylistId = id;
        if (this.stateManager) { this.stateManager.setMusicActivePlaylistId(id); }
        this._renderActivePlaylist();
    }


    /** @private */
    _onAddPlaylistClicked() {
        const url = (this.addPlaylistInputEl.value || '').trim();
        if (!url) { return; }
        if (this.stateManager) {
            this.stateManager.addMusicPlaylist(this._deriveTitle(url), url);
            this.stateManager.setMusicActivePlaylistId(url);
        }
        this.activePlaylistId = url;
        this.addPlaylistInputEl.value = '';
        this.playlists = this._buildPlaylistsModel();
        this._renderSelector();
        this._renderActivePlaylist();
    }


    /** @private */
    _onRemovePlaylistClicked() {
        const pl = this._getActivePlaylist();
        if (!pl || pl.type !== 'm3u') { return; }
        if (this.stateManager) {
            this.stateManager.removeMusicPlaylist(pl.url);
            this.stateManager.setMusicActivePlaylistId(USER_PLAYLIST_ID);
        }
        this.m3uCache.delete(pl.url);
        this.activePlaylistId = USER_PLAYLIST_ID;
        this.playlists = this._buildPlaylistsModel();
        this._renderSelector();
        this._renderActivePlaylist();
    }


    /** @private */
    _onAddUrlClicked() {
        const url = (this.urlInputEl.value || '').trim();
        if (!url) { return; }
        this._addUserTrack(this._deriveTitle(url), url, false);
        this.urlInputEl.value = '';
        this._renderTracks();
    }


    /** @private */
    _onFileChosen() {
        const file = this.fileInputEl.files && this.fileInputEl.files[0];
        if (!file) { return; }
        // object urls are session-only; the track is kept in memory (isLocal=true) and not persisted
        const objectUrl = URL.createObjectURL(file);
        this._addUserTrack(file.name, objectUrl, true);
        this.fileInputEl.value = '';
        this._renderTracks();
    }


    // ==========================================
    // External drop (files / urls -> User Playlist)
    // ==========================================


    /** True if a drag carries external files or url/text (vs an internal row reorder). @private */
    _dropHasFilesOrUrls(e) {
        const dt = e.dataTransfer;
        if (!dt) { return false; }
        const types = Array.from(dt.types || []);
        return types.includes('Files') || types.includes('text/uri-list') || types.includes('text/plain');
    }


    /** True if a dropped file looks like a playable audio/video file (by mime type or extension). @private */
    _isMediaFile(file) {
        if (file.type && (file.type.startsWith('audio/') || file.type.startsWith('video/'))) { return true; }
        // a local file's NAME, not a url: a '#' or '?' in it is part of the name
        const ext = fileNameExtension(file.name);
        return PdfViewer.MEDIA_AUDIO_EXTS.has(ext) || PdfViewer.MEDIA_VIDEO_EXTS.has(ext);
    }


    /**
     * dragover on the track list: allow dropping external files/links (added to the User Playlist).
     * Internal row-reorder drags (this._dragSourceUrl set) are handled per-row and ignored here.
     * @private
     */
    _onExternalDragOver(e) {
        if (this._dragSourceUrl) { return; }
        if (!this._dropHasFilesOrUrls(e)) { return; }
        e.preventDefault();
        if (e.dataTransfer) { e.dataTransfer.dropEffect = 'copy'; }
        if (this.tableWrapEl) { this.tableWrapEl.classList.add('bgmusic-dropzone-active'); }
    }


    /** @private */
    _onExternalDragLeave(e) {
        if (this._dragSourceUrl) { return; }
        // ignore leaves to descendants; only clear when the pointer leaves the wrap entirely
        if (this.tableWrapEl && !this.tableWrapEl.contains(e.relatedTarget)) {
            this.tableWrapEl.classList.remove('bgmusic-dropzone-active');
        }
    }


    /**
     * drop on the track list: add dropped local media files and/or urls to the User Playlist, switching
     * to it so the additions are visible.
     * @private
     */
    _onExternalDrop(e) {
        if (this._dragSourceUrl) { return; }  // internal reorder drop, handled per-row
        const dt = e.dataTransfer;
        if (!dt || !this._dropHasFilesOrUrls(e)) { return; }
        e.preventDefault();
        if (this.tableWrapEl) { this.tableWrapEl.classList.remove('bgmusic-dropzone-active'); }

        let added = false;
        const files = dt.files ? Array.from(dt.files).filter(f => this._isMediaFile(f)) : [];
        if (files.length) {
            // add in reverse so the unshift-based ordering preserves the dropped order at the top
            for (const file of files.slice().reverse()) {
                // object urls are session-only; kept in memory (isLocal=true) and not persisted
                const objectUrl = URL.createObjectURL(file);
                this._addUserTrack(file.name, objectUrl, true);
                added = true;
            }
        } else {
            let text = '';
            try { text = dt.getData('text/uri-list') || dt.getData('text/plain') || ''; } catch (err) { /* ignore */ }
            const urls = text.split(/\r?\n/).map(s => s.trim()).filter(s => s && !s.startsWith('#'));
            for (const u of urls.slice().reverse()) {
                this._addUserTrack(this._deriveTitle(u), u, false);
                added = true;
            }
        }

        if (added) {
            // dropped tracks always land in the User Playlist; switch to it so they are visible
            this.activePlaylistId = USER_PLAYLIST_ID;
            if (this.stateManager) { this.stateManager.setMusicActivePlaylistId(USER_PLAYLIST_ID); }
            this._renderSelector();
            this._renderActivePlaylist();
        }
    }


    // ==========================================
    // Utils
    // ==========================================


    /** Derives a display title from a url's (decoded) file name; the url itself when it has none. @private */
    _deriveTitle(url) {
        if (!url) { return ''; }
        const fileUrl = unwrapViewerLink(url);
        return urlFileName(fileUrl, { decode: true }) || fileUrl;
    }
}
